// Recognizing WHICH FORM a document is, not which copy.
//
// CONSUMER: shared (web prep screen + the landmark builder + node-pool
// tests).
//
// Grant, 2026-10-13, after Nate's dotloop Addendum: "this is a very
// similar experience nate has any time he prepares and send a real
// estate contract document."
//
// WHY THIS EXISTS
//
// The OS holds a reviewed map for 27 AAR forms (the field atlas,
// imported 2026-09-16), and until now it reached one only two ways:
//
//   * document-fingerprint.ts — an exact hash of the first three pages'
//     text. A blank form and a filled one agree on digits, but not on a
//     typed name, a street, a "dotloop signature verification" stamp, a
//     missing cover page or another program's re-print. Every contract
//     Nate actually holds is one of those.
//   * documents.source_form_key — stamped only when the form was copied
//     out of the OS's own library. Uploads and emailed copies never
//     carry it.
//
// Production on 2026-10-13: one of the 27 maps had ever been applied.
// Of ~38 PDFs Realty put on records since 09-01, 3 came from the
// library. Everything else fell to the suggestion ladder, which is the
// experience Nate keeps reporting.
//
// HOW THIS ONE WORKS
//
// A filled copy of an AAR form still prints every word of the form.
// Typing adds text and stamps add text; nothing REMOVES the printed
// words. So instead of asking "is this text identical to the blank?"
// we ask "does this page carry the blank's own printed lines?" — a
// containment test, which extra text cannot break.
//
// For every page of every reviewed form, the landmark builder
// (migration/esign-atlas-import/src/build-form-landmarks.ts) records a
// dozen of that page's distinctive printed lines, with where each sits.
// Here, for a document in hand:
//
//   1. each of its pages is scored against each reviewed page by how
//      many of that page's landmarks it carries;
//   2. a form is RECOGNIZED when most of its pages are found, which also
//      says which of the document's pages is which (a copy missing the
//      purchase contract's Buyer Attachment cover still lines up);
//   3. the printed revision is read off the copy ("Updated: June 2021")
//      and compared — a different revision is reported, not applied,
//      because last year's map on this year's form is a box in the wrong
//      place on a legal document;
//   4. the landmarks that sit in one place on both pages fix how the
//      copy's page is shifted or scaled (dotloop and print-to-PDF both
//      do this), so every box of the map lands where the copy's own
//      lines are.
//
// Pure and dependency-free, the field-types.ts rule.

export interface RecognitionSpan {
  pageNumber: number;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One printed line of a reviewed blank page, and where it sits (PDF
 *  points, top-left origin — the same space render-pdf-pages.ts gives). */
export interface FormLandmark {
  /** Normalized with normalizeFormText. */
  t: string;
  x: number;
  y: number;
}

export interface FormSignaturePage {
  /** The reviewed blank's own page number — the space its map uses. */
  page: number;
  w: number;
  h: number;
  landmarks: FormLandmark[];
}

/** What the OS knows about one reviewed form. Generated (see the
 *  landmark builder) except for `footerNames` on the forms whose blank
 *  PDF reads as glyph codes, which are written by hand. */
export interface FormSignature {
  /** contract_forms.form_key. */
  formKey: string;
  /** For the banner: "Counter Offer". */
  displayName: string;
  /** "june 2021" — normalized month + year, or null when the blank
   *  prints none we can read. */
  revision: string | null;
  /** Which side of a sale the reviewed map is drawn for, when two
   *  catalog forms print identical pages (the agency disclosure prints
   *  the same text for buyer and seller). Null = either. */
  side: "buy" | "sell" | null;
  pages: FormSignaturePage[];
  /** Forms with no usable text in their blank (custom glyph fonts): no
   *  landmarks, so recognized only by the footer the real form prints. */
  footerNames?: string[];
}

export interface PageTransform {
  /** Uniform scale, copy points per blank point. */
  scale: number;
  dx: number;
  dy: number;
}

export interface RecognizedPage {
  /** The reviewed blank's page. */
  formPage: number;
  /** Where that page is in the document in hand. */
  documentPage: number;
  transform: PageTransform;
  /** False when too few landmarks agreed on a position to measure the
   *  page, so the map's own positions are used as they are. The screen
   *  says "check these" rather than presenting them as measured. */
  aligned: boolean;
}

export interface RecognizedForm {
  formKey: string;
  displayName: string;
  /** What the copy prints, when it prints a revision at all. */
  documentRevision: string | null;
  /** What the reviewed map was drawn on. */
  mapRevision: string | null;
  /** True when the map can be applied: same revision, or the copy prints
   *  none that contradicts it. */
  revisionMatches: boolean;
  pages: RecognizedPage[];
}

const MONTHS =
  "january|february|march|april|may|june|july|august|september|october|november|december";

/**
 * One spelling for every producer's version of the same printed words.
 *
 * Exported because the landmark builder MUST normalize exactly as the
 * matcher does — two copies of this would drift, and a drift here reads
 * as "the form is not recognized" with nothing to say why.
 */
export function normalizeFormText(raw: string): string {
  return (
    raw
      // Ligatures (ﬁ, ﬂ) and full-width forms, which one producer emits
      // and another doesn't.
      .normalize("NFKC")
      .toLowerCase()
      .replace(/[’‘`´]/g, "'")
      .replace(/[“”"]/g, "'")
      .replace(/[®™©]/g, " ")
      .replace(/[–—]/g, "-")
      .replace(/\s*[•·]\s*/g, " • ")
      .replace(/\s+/g, " ")
      .trim()
  );
}

/** A page's spans grouped into printed lines, left to right. pdf.js
 *  splits a line into runs differently per producer; a line is the unit
 *  two producers agree on. */
interface PageLine {
  text: string;
  x: number;
  y: number;
}

/** Spans whose tops are this close are on one printed line. Body type
 *  on AAR forms is 8–10pt with ~11pt leading, so half the leading. */
const SAME_LINE_PT = 2.5;

export function pageLines(spans: RecognitionSpan[]): Map<number, PageLine[]> {
  const byPage = new Map<number, RecognitionSpan[]>();
  for (const s of spans) {
    if (!s.text.trim()) continue;
    const list = byPage.get(s.pageNumber) ?? [];
    list.push(s);
    byPage.set(s.pageNumber, list);
  }
  const out = new Map<number, PageLine[]>();
  for (const [page, list] of byPage) {
    const sorted = [...list].sort((a, b) => a.y - b.y || a.x - b.x);
    const lines: { y: number; parts: RecognitionSpan[] }[] = [];
    for (const s of sorted) {
      const line = lines.find((l) => Math.abs(l.y - s.y) < SAME_LINE_PT);
      if (line) line.parts.push(s);
      else lines.push({ y: s.y, parts: [s] });
    }
    out.set(
      page,
      lines.map((l) => {
        const parts = [...l.parts].sort((a, b) => a.x - b.x);
        return {
          text: normalizeFormText(parts.map((p) => p.text).join(" ")),
          x: parts[0]!.x,
          y: l.y,
        };
      }),
    );
  }
  return out;
}

/** The revision a page prints, "june 2021", or null. AAR prints it in
 *  the footer ("Counter Offer • Updated: June 2021 • Copyright …") and
 *  most forms repeat it under "Document updated:" in the header. */
export function revisionPrinted(lines: PageLine[]): string | null {
  const joined = lines.map((l) => l.text).join(" ");
  const footer = joined.match(
    new RegExp(`•\\s*(?:updated:?\\s*)?(${MONTHS})\\s+((?:19|20)\\d{2})\\s*•\\s*copyright`),
  );
  if (footer) return `${footer[1]} ${footer[2]}`;
  const header = joined.match(new RegExp(`(?:document\\s+)?updated:?\\s*(${MONTHS})\\s+((?:19|20)\\d{2})`));
  return header ? `${header[1]} ${header[2]}` : null;
}

/** The form name a page's AAR footer prints, normalized, or null:
 *  "counter offer • updated: june 2021 • copyright" → "counter offer". */
export function footerNamePrinted(lines: PageLine[]): string | null {
  for (const l of lines) {
    const m = l.text.match(
      new RegExp(`^(.*?)\\s*•\\s*(?:updated:?\\s*)?(?:${MONTHS})\\s+(?:19|20)\\d{2}\\b`),
    );
    if (m && m[1]!.trim()) return m[1]!.trim();
  }
  return null;
}

/** Of a page's landmarks, how many the document page carries. A
 *  landmark is carried when one of the page's printed lines contains it
 *  — containment, so a producer that runs two lines together, or a
 *  value typed on the end of a caption, still counts. */
function landmarksCarried(landmarks: FormLandmark[], lines: PageLine[]): number {
  let n = 0;
  for (const lm of landmarks) {
    if (lines.some((l) => l.text.includes(lm.t))) n++;
  }
  return n;
}

/**
 * A blank page is FOUND on a document page when at least this share of
 * its landmarks are there (and never fewer than MIN_LANDMARKS_CARRIED).
 *
 * DECISION: 0.6, measured against the reviewed blanks themselves —
 * every page of every form scores 1.0 against itself and no page of one
 * form reaches 0.4 against another (tests/unit/esign-form-recognition
 * checks the whole catalog both ways). A filled copy loses nothing
 * printed, so it scores like the blank; the headroom is for a
 * producer that splits or joins lines differently.
 */
const PAGE_MATCH_SHARE = 0.6;
const MIN_LANDMARKS_CARRIED = 3;

/** A form is recognized when this share of its pages are found. Not
 *  1.0: the purchase contract's Buyer Attachment cover is routinely
 *  left off a copy, and that must not make the other ten pages
 *  unrecognizable. */
const FORM_MATCH_SHARE = 0.5;

/**
 * Fit the copy's page onto the blank's from the landmarks that sit on a
 * single printed line in both: uniform scale plus offset, least squares.
 *
 * Uniform because a re-print scales a page as a whole; nothing in the
 * real world stretches an AAR form in one direction only, and allowing
 * it would let two bad matches invent a distortion.
 */
export function fitPageTransform(
  landmarks: FormLandmark[],
  lines: PageLine[],
): { transform: PageTransform; aligned: boolean } {
  const identity = { transform: { scale: 1, dx: 0, dy: 0 }, aligned: false };
  // Only lines that ARE the landmark (not merely contain it) give a
  // position: a line holding the landmark plus a typed value starts
  // where the landmark does, but a line two producers ran together
  // starts wherever the first of them does.
  const pairs: { bx: number; by: number; cx: number; cy: number }[] = [];
  for (const lm of landmarks) {
    const hits = lines.filter((l) => l.text === lm.t);
    if (hits.length !== 1) continue;
    pairs.push({ bx: lm.x, by: lm.y, cx: hits[0]!.x, cy: hits[0]!.y });
  }
  if (pairs.length < 3) return identity;

  // Scale from the spread of y (every AAR page has landmarks top to
  // bottom; x spread is often small because lines start at one margin).
  const n = pairs.length;
  const mbx = pairs.reduce((a, p) => a + p.bx, 0) / n;
  const mby = pairs.reduce((a, p) => a + p.by, 0) / n;
  const mcx = pairs.reduce((a, p) => a + p.cx, 0) / n;
  const mcy = pairs.reduce((a, p) => a + p.cy, 0) / n;
  let num = 0;
  let den = 0;
  for (const p of pairs) {
    num += (p.bx - mbx) * (p.cx - mcx) + (p.by - mby) * (p.cy - mcy);
    den += (p.bx - mbx) ** 2 + (p.by - mby) ** 2;
  }
  if (den < 1) return identity;
  const scale = num / den;
  // A real re-print scales within a few percent of letter. Anything
  // further is a mismatch pretending to be a fit.
  if (!(scale > 0.8 && scale < 1.25)) return identity;
  const dx = mcx - scale * mbx;
  const dy = mcy - scale * mby;

  // Every pair must land within a few points of where the fit says;
  // otherwise one landmark matched the wrong line and the fit is wrong.
  const worst = Math.max(
    ...pairs.map((p) => Math.hypot(scale * p.bx + dx - p.cx, scale * p.by + dy - p.cy)),
  );
  if (worst > 4) return identity;

  // Near-identity is reported as identity, so a copy that is the blank
  // (or a faithful print of it) does not move every box by 0.2pt.
  if (Math.abs(scale - 1) < 0.002 && Math.abs(dx) < 0.75 && Math.abs(dy) < 0.75) {
    return { transform: { scale: 1, dx: 0, dy: 0 }, aligned: true };
  }
  return { transform: { scale, dx, dy }, aligned: true };
}

export interface RecognizeOptions {
  /** The record's side, used only to break a tie between two catalog
   *  forms that print the same pages. */
  side?: "buy" | "sell" | null;
}

/**
 * Every reviewed form the document carries, with which of its pages is
 * which. Usually one; a dotloop export of a whole packet can carry
 * several, and each gets its own map on its own pages.
 */
export function recognizeForms(
  spans: RecognitionSpan[],
  catalog: FormSignature[],
  options: RecognizeOptions = {},
): RecognizedForm[] {
  const linesByPage = pageLines(spans);
  const documentPages = [...linesByPage.keys()].sort((a, b) => a - b);

  interface Candidate {
    sig: FormSignature;
    /** formPage → documentPage. */
    assignment: Map<number, number>;
    score: number;
  }
  const candidates: Candidate[] = [];

  for (const sig of catalog) {
    if (sig.pages.length === 0 || sig.pages.every((p) => p.landmarks.length === 0)) {
      const viaFooter = matchByFooter(sig, linesByPage, documentPages);
      if (viaFooter) candidates.push(viaFooter);
      continue;
    }
    const assignment = new Map<number, number>();
    let score = 0;
    const used = new Set<number>();
    for (const page of sig.pages) {
      const need = Math.max(MIN_LANDMARKS_CARRIED, Math.ceil(page.landmarks.length * PAGE_MATCH_SHARE));
      if (page.landmarks.length < MIN_LANDMARKS_CARRIED) continue;
      let best: { doc: number; carried: number } | null = null;
      for (const doc of documentPages) {
        if (used.has(doc)) continue;
        const carried = landmarksCarried(page.landmarks, linesByPage.get(doc) ?? []);
        if (carried >= need && (!best || carried > best.carried)) best = { doc, carried };
      }
      if (best) {
        assignment.set(page.page, best.doc);
        used.add(best.doc);
        score += best.carried / page.landmarks.length;
      }
    }
    const countable = sig.pages.filter((p) => p.landmarks.length >= MIN_LANDMARKS_CARRIED).length;
    if (countable > 0 && assignment.size / countable >= FORM_MATCH_SHARE) {
      candidates.push({ sig, assignment, score });
    }
  }

  // TWINS: two catalog forms that print the very same pages (the agency
  // disclosure, once for a buyer and once for a seller, mapped to
  // different seats). They score identically on the same document
  // pages, so the page text cannot choose; the record's side does, and
  // when it can't either, NEITHER is applied — the wrong side's map is a
  // signature on the other party's line.
  const resolved: Candidate[] = [];
  const seen = new Set<Candidate>();
  for (const c of candidates) {
    if (seen.has(c)) continue;
    const pagesOf = (x: Candidate) => [...x.assignment.values()].sort((a, b) => a - b).join(",");
    const group = candidates.filter(
      (o) => Math.abs(o.score - c.score) < 1e-9 && pagesOf(o) === pagesOf(c),
    );
    for (const g of group) seen.add(g);
    if (group.length === 1) {
      resolved.push(c);
      continue;
    }
    const bySide = options.side ? group.filter((g) => g.sig.side === options.side) : [];
    if (bySide.length === 1) resolved.push(bySide[0]!);
  }

  // Strongest first; a document page belongs to one form.
  resolved.sort((a, b) => b.score - a.score || b.assignment.size - a.assignment.size);
  const claimed = new Set<number>();
  const out: RecognizedForm[] = [];
  for (const c of resolved) {
    const docPages = [...c.assignment.values()];
    if (docPages.some((d) => claimed.has(d))) continue;
    for (const d of docPages) claimed.add(d);
    out.push(finishRecognition(c.sig, c.assignment, linesByPage, claimed));
  }
  return out.sort((a, b) => (a.pages[0]?.documentPage ?? 0) - (b.pages[0]?.documentPage ?? 0));
}

/** The forms whose blank has no readable text: found by the footer name
 *  the real form prints, pages taken in order. No landmarks means no
 *  alignment, so every page is reported unaligned. */
function matchByFooter(
  sig: FormSignature,
  linesByPage: Map<number, PageLine[]>,
  documentPages: number[],
): { sig: FormSignature; assignment: Map<number, number>; score: number } | null {
  const names = sig.footerNames ?? [];
  if (names.length === 0) return null;
  const docs = documentPages.filter((d) => {
    const name = footerNamePrinted(linesByPage.get(d) ?? []);
    return name !== null && names.includes(name);
  });
  if (docs.length === 0) return null;
  const formPages = sig.pages.length > 0 ? sig.pages.map((p) => p.page) : [1];
  const assignment = new Map<number, number>();
  formPages.forEach((fp, i) => {
    if (docs[i] !== undefined) assignment.set(fp, docs[i]!);
  });
  return { sig, assignment, score: assignment.size * 0.5 };
}

/**
 * A reviewed page with no landmark of its own (the purchase contract's
 * page 9 is nothing but blanks and lines every other page also prints)
 * is placed by its neighbours: if the page before it is document page
 * d, it is d + 1, and the same from the page after. Only into a page no
 * other form owns, and only when that page exists. Its position is then
 * borrowed from the neighbour too, and reported unaligned.
 */
function fillGapPages(
  sig: FormSignature,
  assignment: Map<number, number>,
  documentPages: Set<number>,
  claimed: Set<number>,
): Map<number, number> {
  const out = new Map(assignment);
  const taken = new Set([...out.values(), ...claimed]);
  const formPages = sig.pages.map((p) => p.page).sort((a, b) => a - b);
  let changed = true;
  while (changed) {
    changed = false;
    for (const fp of formPages) {
      if (out.has(fp)) continue;
      const before = out.get(fp - 1);
      const after = out.get(fp + 1);
      const guess = before !== undefined ? before + 1 : after !== undefined ? after - 1 : undefined;
      if (guess === undefined || !documentPages.has(guess) || taken.has(guess)) continue;
      out.set(fp, guess);
      taken.add(guess);
      changed = true;
    }
  }
  return out;
}

function finishRecognition(
  sig: FormSignature,
  measured: Map<number, number>,
  linesByPage: Map<number, PageLine[]>,
  claimed: Set<number>,
): RecognizedForm {
  const assignment = fillGapPages(sig, measured, new Set(linesByPage.keys()), claimed);
  for (const d of assignment.values()) claimed.add(d);
  const pages: RecognizedPage[] = [];
  let documentRevision: string | null = null;
  for (const [formPage, documentPage] of [...assignment].sort((a, b) => a[0] - b[0])) {
    const lines = linesByPage.get(documentPage) ?? [];
    documentRevision ??= revisionPrinted(lines);
    const page = sig.pages.find((p) => p.page === formPage);
    const fit =
      page && measured.has(formPage)
        ? fitPageTransform(page.landmarks, lines)
        : { transform: { scale: 1, dx: 0, dy: 0 }, aligned: false };
    pages.push({ formPage, documentPage, transform: fit.transform, aligned: fit.aligned });
  }
  // A page placed by its neighbours, or one whose own landmarks were too
  // few to measure, takes the nearest measured page's position: a
  // re-print moves every page of a document the same way. Still
  // reported unaligned, so the screen asks for a look.
  for (const pg of pages) {
    if (pg.aligned) continue;
    const nearest = pages
      .filter((o) => o.aligned)
      .sort((a, b) => Math.abs(a.formPage - pg.formPage) - Math.abs(b.formPage - pg.formPage))[0];
    if (nearest) pg.transform = { ...nearest.transform };
  }
  // A copy that prints no revision cannot contradict the map; one that
  // prints a different one does.
  const revisionMatches =
    documentRevision === null || sig.revision === null || documentRevision === sig.revision;
  return {
    formKey: sig.formKey,
    displayName: sig.displayName,
    documentRevision,
    mapRevision: sig.revision,
    revisionMatches,
    pages,
  };
}

/** A box from a reviewed map, moved onto the document in hand. Null
 *  when the map's page is not in this document. */
export function placeOnDocument<T extends { pageNumber: number; x: number; y: number; width: number; height: number }>(
  box: T,
  form: RecognizedForm,
): T | null {
  const page = form.pages.find((p) => p.formPage === box.pageNumber);
  if (!page) return null;
  const { scale, dx, dy } = page.transform;
  return {
    ...box,
    pageNumber: page.documentPage,
    x: box.x * scale + dx,
    y: box.y * scale + dy,
    width: box.width * scale,
    height: box.height * scale,
  };
}

/** Month + year as the banner prints it: "june 2021" → "June 2021". */
export function revisionForDisplay(revision: string | null): string | null {
  if (!revision) return null;
  return revision.replace(/^([a-z])/, (c) => c.toUpperCase());
}
